import { inject } from '@angular/core';
import { CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { ModalController } from '@ionic/angular/standalone';
import { LoginModalComponent } from 'src/app/components/login-modal/login-modal.component';
import { LanguageService } from '../language.service';
import { PlatformService } from '../platform.service';
import { AuthService } from './auth.service';
import { LoadingController } from '@ionic/angular';

export const authGuard: CanActivateFn = async (_: any, state: RouterStateSnapshot) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const languageService = inject(LanguageService);
  const platformService = inject(PlatformService);
  const modalController = inject(ModalController);
  const loadingController = inject(LoadingController);

  const decodedToken = await authService.getDecodedToken();

  if (decodedToken && !authService.isTokenExpired(decodedToken)) {
    return true;
  }

  const loading = await loadingController.create();
  await loading.present();

  // refreshToken() validates the refresh token and coalesces concurrent
  // callers onto a single in-flight request, so a refresh already started
  // elsewhere is awaited here too instead of the app firing a second one and
  // invalidating its own tokens. A missing/expired refresh token rejects,
  // falling through to the login flow below.
  try {
    await authService.refreshToken();

    return Boolean(await authService.isAuthenticated());
  } catch (error) {
    authService.logout();
  } finally {
    loading.dismiss();
  }

  if (!platformService.isMobile) {
    return router.createUrlTree(['/', languageService.locale(), 'login'], {
      queryParams: { redirect: encodeURIComponent(state.url) },
    });
  }

  const modal = await modalController.create({
    component: LoginModalComponent,
    cssClass: 'login-modal',
    canDismiss: true,
    breakpoints: [0, 0.75, 1],
    initialBreakpoint: 0.75,
  });

  await modal.present();
  await modal.onDidDismiss();

  return Boolean(await authService.isAuthenticated());
};

export const loginGuard: CanActivateFn = async () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const languageService = inject(LanguageService);
  const platformService = inject(PlatformService);

  if (await authService.isAuthenticated()) {
    return router.createUrlTree(['/', languageService.locale(), 'home']);
  }

  if (!platformService.isMobile) {
    return true;
  }

  return router.createUrlTree(['/', languageService.locale(), 'home']);
};

export const logoutGuard: CanActivateFn = async () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const languageService = inject(LanguageService);

  await authService.logout();

  return router.createUrlTree(['/', languageService.locale(), 'home']);
};
